import { useEffect } from "react";
import { Link } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Skeleton } from "@/components/ui/Skeleton";
import { ApiError } from "@/lib/api";

import { DeliveryAddressInput } from "../service_area/DeliveryAddressInput";
import { hashAddress, track } from "../lib/analytics";
import { usePublicCategories } from "./api";
import type { CategorySummary } from "./types";

// FR-2 — top-of-funnel home page. Hero with the parallel-process
// DeliveryAddressInput, then category grid, "Why JDR" trust strip,
// footer (rendered by the shell). Brand voice: imperative, no
// exclamations except the single hero headline allowance.
export function HomePage(): JSX.Element {
  useEffect(() => {
    track({ name: "home_viewed" });
  }, []);

  const { data, isPending, isError, error } = usePublicCategories();

  return (
    <div className="space-y-10">
      <section
        aria-labelledby="hero-heading"
        className="rounded-lg bg-brand-black p-6 text-brand-white shadow-sm md:p-10"
      >
        {/* Phase 4e brand-visual-rollout FR-15 — the ONE flag-navy
            accent placement in the hero. Single pill, no flag fill,
            no stars; just the brand-allowed accent color. The
            data-brand-accent attribute is what the Jest snapshot
            counts to enforce "exactly one". */}
        <span
          data-brand-accent="flag-navy"
          className="inline-block rounded-md bg-flag-navy px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-white"
        >
          Family-owned · Colorado-based
        </span>
        <h1 id="hero-heading" className="mt-3 text-3xl font-semibold italic md:text-4xl">
          Rent contractor-grade equipment.
        </h1>
        <p className="mt-2 max-w-xl text-base text-zinc-200">
          Delivered to your jobsite in Colorado.
        </p>
        <div className="mt-6 max-w-xl rounded-md bg-brand-white p-4 text-brand-black">
          <DeliveryAddressInput
            label="Check delivery to your jobsite"
            onAddressChange={(addr) => {
              // Fire the analytics event only when the user actually
              // submits — DeliveryAddressInput's internal debounce
              // owns the call; we don't want a track-per-keystroke.
              if (addr.trim().length > 0) {
                track({ name: "service_area_checked", address_hash: hashAddress(addr) });
              }
            }}
          />
        </div>
      </section>

      <section aria-labelledby="categories-heading" className="space-y-4">
        <h2 id="categories-heading" className="text-xl font-semibold text-brand-black">
          Browse categories
        </h2>

        {isError ? (
          <Alert variant="destructive">
            We couldn&rsquo;t load categories.
            {error instanceof ApiError ? ` (HTTP ${error.status})` : null}
          </Alert>
        ) : null}

        {isPending ? <CategoryGridSkeleton /> : null}

        {data && data.length > 0 ? <CategoryGrid categories={data} /> : null}

        {data && data.length === 0 && !isError ? (
          <p className="text-sm text-zinc-600">No categories yet. Check back soon.</p>
        ) : null}
      </section>

      <section aria-labelledby="why-heading" className="space-y-4">
        <h2 id="why-heading" className="text-xl font-semibold text-brand-black">
          Why JDR
        </h2>
        <ul className="grid gap-3 sm:grid-cols-2">
          <WhyItem
            title="American family-owned"
            body="Independent. Owner-operated, direct line, no call centers."
          />
          <WhyItem
            title="Colorado-based"
            body="Same-day quotes, next-day delivery across our service area."
          />
          <WhyItem
            title="Insured contractors only"
            body="We verify your Certificate of Insurance before dispatch."
          />
          <WhyItem
            title="Contractor-grade, jobsite-ready"
            body="Fueled, greased, and inspected before it goes out the door."
          />
        </ul>
      </section>
    </div>
  );
}

function CategoryGrid({ categories }: { categories: CategorySummary[] }): JSX.Element {
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {categories.map((c) => (
        <li key={c.id}>
          <Link
            to={`/c/${c.slug}`}
            className="block h-full overflow-hidden rounded-md border border-zinc-200 bg-brand-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2"
          >
            <div className="aspect-[4/3] w-full overflow-hidden bg-zinc-100">
              {c.hero_image_url ? (
                <img
                  src={c.hero_image_url}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-zinc-400">
                  <span className="text-2xl font-bold tracking-tight">JDR</span>
                </div>
              )}
            </div>
            <div className="p-3">
              <p className="text-base font-semibold text-brand-black">{c.name}</p>
              {/* Phase 4e CO-only storefront FR-20: drop the "type"/"types"
                  noun here so the count carries the meaning alongside the
                  category name above. The follow-on terminology slice
                  (4e-equipment-terminology) may refine to a different
                  noun if the comms persona prefers explicit wording. */}
              <p className="mt-0.5 text-sm text-zinc-600">
                {c.type_count} listed
              </p>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function CategoryGridSkeleton(): JSX.Element {
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-busy="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i}>
          <Skeleton className="h-48" />
        </li>
      ))}
    </ul>
  );
}

function WhyItem({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <li className="rounded-md border border-zinc-200 bg-brand-white p-4">
      <p className="font-semibold text-brand-black">{title}</p>
      <p className="mt-1 text-sm text-zinc-600">{body}</p>
    </li>
  );
}
